import { PlanFeatures, PlanLimits } from './types';

/**
 * The one list of plan-gated features the two plan surfaces render — the
 * public pricing page and the internal plan picker. One order and one
 * wording, so both pages tell the same story about the same plan row, and a
 * feature added to FEATURE_KEYS costs exactly one label here (CLAUDE.md,
 * "A plan is shown in more places than it is enforced").
 *
 * AI leads because it is the differentiator a shopper scans for first; the
 * rest keep FEATURE_KEYS order.
 */
export const PLAN_FEATURE_ORDER: (keyof PlanFeatures)[] = [
  'aiBuilder', 'sso', 'api', 'webhooks', 'customBranding', 'teamAccess', 'hideAdvertisement', 'smartPdf',
];

/** What each feature is called where a customer chooses a plan. */
export const PLAN_FEATURE_LINE: Record<keyof PlanFeatures, string> = {
  aiBuilder: 'AI form builder',
  sso: 'Single sign-on (OIDC / SAML)',
  api: 'API access',
  webhooks: 'Account webhooks',
  customBranding: 'Custom branding and custom domains',
  teamAccess: 'Team access — folders, roles and sharing',
  hideAdvertisement: 'Remove the Form-Magic badge',
  smartPdf: 'Smart PDF — your own PDF template',
};

/**
 * The AI allowance as a card or table cell says it: "25 / month", "Unlimited",
 * or null when the plan has no usable access — the feature switched off, or a
 * zero ceiling, which is no access with extra steps.
 */
export function aiAllowance(features: PlanFeatures, limits: PlanLimits): string | null {
  if (features.aiBuilder !== true) return null;
  const n = limits.aiGenerationsPerMonth;
  if (n === null) return 'Unlimited';
  if (n === 0) return null;
  return `${n.toLocaleString('en-US')} / month`;
}

/**
 * The form assistant (068) — "Ask AI" inside a form. It rides the aiBuilder
 * feature but has a monthly ceiling of its own, so it is a line of its own
 * directly under the builder's on every plan surface: the pricing cards, the
 * picker's cards and its side-by-side table.
 */
export const AI_ASSISTANT_LINE = 'AI assistant in every form';

/** "200 questions / month", "Unlimited", or null when the plan has no usable access. */
export function assistantAllowance(features: PlanFeatures, limits: PlanLimits): string | null {
  if (features.aiBuilder !== true) return null;
  const n = limits.aiAssistantMessagesPerMonth;
  if (n === null) return 'Unlimited';
  if (!n) return null;
  return `${n.toLocaleString('en-US')} questions / month`;
}

/** The assistant's line, shaped like `featureLine`'s. */
export function assistantLine(features: PlanFeatures, limits: PlanLimits): { text: string; has: boolean } {
  const allowance = assistantAllowance(features, limits);
  return allowance === null
    ? { text: AI_ASSISTANT_LINE, has: false }
    : { text: `${AI_ASSISTANT_LINE} · ${allowance}`, has: true };
}

/**
 * One feature line: its text and whether this plan has it. The AI line
 * carries its monthly allowance, because access without the number undersells
 * the bigger tiers.
 */
export function featureLine(
  key: keyof PlanFeatures, features: PlanFeatures, limits: PlanLimits,
): { text: string; has: boolean } {
  if (key === 'aiBuilder') {
    const allowance = aiAllowance(features, limits);
    return allowance === null
      ? { text: PLAN_FEATURE_LINE.aiBuilder, has: false }
      : { text: `${PLAN_FEATURE_LINE.aiBuilder} · ${allowance}`, has: true };
  }
  return { text: PLAN_FEATURE_LINE[key], has: features[key] === true };
}
